<template>
	<v-card>
		<v-card-title class="dialog-header sgnl-subtitle-medium">
			Manage Subscription
			<v-spacer />
			<v-btn
				icon
				variant="text"
				size="small"
				@click="$emit('close-dialog')"
			>
				<Close16 />
			</v-btn>
		</v-card-title>
		<v-divider />
		<v-card-text class="dialog-body">
			<v-form
				v-model="formIsValid"
				@submit.prevent="onSubmit"
				ref="manageSubscriptionForm"
				action="/"
				class="stack"
			>
				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.fullname }}</div>
					<v-text-field
						v-model="formValues.fullname"
						name="name"
						autocomplete="name"
						:rules="formRules.fullname"
						validate-on-blur
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.email }}</div>
					<v-text-field
						v-model="formValues.email"
						type="email"
						name="email"
						autocomplete="email"
						:rules="formRules.email"
						required
						validate-on-blur
						disabled
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.requests }}</div>
					<v-textarea
						v-model="formValues.requests"
						rows="6"
						auto-grow
						:rules="formRules.requests"
						hint="Let us know what changes you'd like to make to your subscription or any questions you have."
						persistent-hint
						hide-details="auto"
					/>
				</div>
			</v-form>
		</v-card-text>
		<v-divider />
		<v-card-actions class="dialog-footer">
			<v-btn
				variant="outlined"
				@click="$emit('close-dialog')"
				>Cancel</v-btn
			>
			<v-btn
				variant="flat"
				color="primary"
				:loading="submitButtonLoading"
				:disabled="!formIsValid"
				@click="onSubmit"
				>Submit</v-btn
			>
		</v-card-actions>
	</v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { fetchAuthSession } from "aws-amplify/auth";
import { joinWaitlist } from "@/js/signaloidClient";
import { monitoringCaptureError } from "@/plugins/monitoring";
import Close16 from "@carbon/icons-vue/es/close/16";

export default defineComponent({
	name: "ManageSubscriptionForm",
	components: { Close16 },
	emits: ["form-submitted", "form-submission-failed", "close-dialog"],
	data: () => ({
		formIsValid: false,
		submitButtonLoading: false,
		formPostSuccess: false,
		formValues: {
			fullname: "",
			email: "",
			currentTier: "",
			requests: "",
		},
		formRules: {
			fullname: [],
			email: [
				(v: string) => (v && v.length > 0) || "This field is required.",
				(v: string) => /.+@.+\..+/.test(v) || "Please enter a valid email.",
			],
			requests: [(v: string) => (v && v.length > 0) || "This field is required."],
		},
		labelValues: {
			fullname: "Full Name",
			email: "Email",
			requests: "Requests",
		},
	}),
	props: {
		passedFullname: { type: String, default: null },
		passedEmail: { type: String, default: null },
		passedCurrentTier: { type: String, default: null },
	},
	watch: {
		passedCurrentTier: function () {
			this.registerPassedProps();
		},
	},
	mounted() {
		this.registerPassedProps();
	},
	methods: {
		registerPassedProps() {
			this.formValues.fullname = this.passedFullname || "";
			this.formValues.email = this.passedEmail || "";
			this.formValues.currentTier = this.passedCurrentTier || "";
		},
		async onSubmit() {
			this.formPostSuccess = false;
			// @ts-ignore
			if (!this.$refs.manageSubscriptionForm.validate()) {
				return;
			}

			const message: Record<string, any> = {};
			message["manage-subscription-form"] = this.formValues;
			message["form-labels"] = this.labelValues;

			/*
			 *  Build a pretty version of the message.
			 */
			let messagePretty = "";
			messagePretty += "A user has submitted a Manage Subscription request.\n\n";
			messagePretty += `Current Tier: ${this.formValues.currentTier}\n`;
			messagePretty += `Full Name: ${this.formValues.fullname}\n`;
			messagePretty += `Email: ${this.formValues.email}\n`;
			messagePretty += `\nRequests:\n${this.formValues.requests}\n`;

			try {
				const session = await fetchAuthSession();
				const idToken = session.tokens?.idToken;
				messagePretty += "\n# User Metadata\n";
				messagePretty += `CognitoUser email: ${idToken?.payload?.["cognito:username"] ?? ""}\n`;
				messagePretty += `CognitoUser sub: ${idToken?.payload?.["sub"] ?? ""}\n`;
				messagePretty += `CognitoUser name: ${idToken?.payload?.["name"] ?? ""}\n`;
				messagePretty += `CognitoUser groups: ${idToken?.payload?.["cognito:groups"] ?? ""}\n`;
			} catch (error) {
				monitoringCaptureError(error, "Submit manage subscription form");
			}

			try {
				this.submitButtonLoading = true;
				await joinWaitlist(messagePretty);
				// @ts-ignore
				this.$refs.manageSubscriptionForm.reset();
				// @ts-ignore
				this.$posthog?.capture("submitted_manage_subscription_form", {
					currentTier: this.formValues.currentTier,
				});

				// Emit event to close dialog and show snackbar
				this.$emit("form-submitted");
			} catch (error) {
				monitoringCaptureError(error, "Submit manage subscription form");
				// Emit failure event
				this.$emit("form-submission-failed");
			} finally {
				this.submitButtonLoading = false;
			}
		},
	},
});
</script>
